import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Topic, UpdateTopic } from '@mmf/contracts';
import { deleteTopic, fetchComments, fetchTopic, updateTopic } from '../api/client';
import { useMe } from '../auth/useMe';
import { AttachmentList } from './AttachmentList';
import { AttachmentUpload } from './AttachmentUpload';
import { CommentThread } from './CommentThread';
import { moderationBadgeText } from './moderationBadge';
import { ReportForm } from './ReportForm';
import { VoteButtons } from './VoteButtons';

export function TopicDetail(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { principal } = useMe();

  const topic = useQuery({
    queryKey: ['topic', id],
    queryFn: () => fetchTopic(id as string),
    enabled: Boolean(id),
  });

  const comments = useQuery({
    queryKey: ['comments', id],
    queryFn: () => fetchComments(id as string),
    enabled: Boolean(id),
  });

  if (!id) {
    return (
      <p role="alert" data-testid="topic-error">
        No discussion specified.
      </p>
    );
  }

  if (topic.isLoading) {
    return <p role="status">Loading…</p>;
  }

  if (topic.error || !topic.data) {
    return (
      <p role="alert" data-testid="topic-error">
        Could not load this discussion.
      </p>
    );
  }

  const isOwnTopic = principal?.userId === topic.data.authorId;

  return (
    <article aria-labelledby="topic-title">
      <Link to={`/discussions/${encodeURIComponent(topic.data.mainCategoryId)}`} className="back-link">
        ← Back to {topic.data.mainCategoryName}
      </Link>

      <p className="hint">{topic.data.categoryName}</p>
      <h2 id="topic-title" data-testid="topic-title">
        {topic.data.title}
      </h2>
      <p className="hint">
        Posted by {topic.data.authorDisplayName}
        {topic.data.updatedAt.getTime() !== topic.data.createdAt.getTime() && (
          <span data-testid="topic-edited-marker"> · (edited)</span>
        )}
        {moderationBadgeText(topic.data.moderationState) && (
          <span className="badge" data-testid="topic-moderation-badge">
            {' '}
            · {moderationBadgeText(topic.data.moderationState)}
          </span>
        )}
      </p>

      <TopicBody topic={topic.data} id={id} isOwnTopic={isOwnTopic} principalId={principal?.userId} />

      <hr />

      <CommentThread
        topicId={id}
        comments={comments.data ?? []}
        isLoading={comments.isLoading}
        error={Boolean(comments.error)}
        principalId={principal?.userId}
      />
    </article>
  );
}

function TopicBody({
  topic,
  id,
  isOwnTopic,
  principalId,
}: {
  topic: Topic;
  id: string;
  isOwnTopic: boolean;
  principalId: string | undefined;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [title, setTitle] = useState(topic.title);
  const [body, setBody] = useState(topic.body);

  const update = useMutation({
    mutationFn: (input: UpdateTopic) => updateTopic(id, input),
    onSuccess: async () => {
      setIsEditing(false);
      await queryClient.invalidateQueries({ queryKey: ['topic', id] });
      await queryClient.invalidateQueries({ queryKey: ['topics'] });
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteTopic(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['topics'] });
      navigate(`/discussions/${encodeURIComponent(topic.mainCategoryId)}`);
    },
  });

  if (isEditing) {
    return (
      <form
        className="new-topic-form"
        onSubmit={(event) => {
          event.preventDefault();
          update.mutate({ title, body });
        }}
      >
        <label htmlFor="edit-topic-title">Title</label>
        <input id="edit-topic-title" value={title} required onChange={(event) => setTitle(event.target.value)} />

        <label htmlFor="edit-topic-body">Body</label>
        <textarea
          id="edit-topic-body"
          value={body}
          rows={6}
          required
          onChange={(event) => setBody(event.target.value)}
        />

        {update.error && (
          <p role="alert" className="notice warning">
            Could not save your changes. Try again.
          </p>
        )}

        <div className="row">
          <button
            type="submit"
            className="button"
            disabled={update.isPending || title.trim() === '' || body.trim() === ''}
            data-testid="save-topic-edit"
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="button secondary"
            onClick={() => {
              setTitle(topic.title);
              setBody(topic.body);
              setIsEditing(false);
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    );
  }

  return (
    <>
      <div className="topic-body" data-testid="topic-body">
        {topic.body}
      </div>

      <AttachmentList kind="topic" id={topic.id} principalId={principalId} />

      <div className="comment-actions">
        <VoteButtons
          kind="topic"
          id={topic.id}
          score={topic.voteScore}
          myVote={topic.myVote}
          invalidateKeys={[
            ['topic', id],
            ['topics'],
          ]}
        />

        {isOwnTopic && (
          <button type="button" className="button secondary small" onClick={() => setIsEditing(true)} data-testid="edit-topic">
            Edit
          </button>
        )}

        {isOwnTopic && !isConfirmingDelete && (
          <button
            type="button"
            className="button secondary small"
            onClick={() => setIsConfirmingDelete(true)}
            data-testid="delete-topic"
          >
            Delete
          </button>
        )}

        <ReportForm kind="topic" id={topic.id} />
      </div>

      {isConfirmingDelete && (
        <p className="notice warning">
          Delete this discussion? This can't be undone.{' '}
          <button
            type="button"
            className="button small"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
            data-testid="confirm-delete-topic"
          >
            {remove.isPending ? 'Deleting…' : 'Confirm delete'}
          </button>{' '}
          <button type="button" className="button secondary small" onClick={() => setIsConfirmingDelete(false)}>
            Cancel
          </button>
        </p>
      )}

      {remove.error && (
        <p role="alert" className="notice warning">
          Could not delete this discussion. Try again.
        </p>
      )}

      {isOwnTopic && (
        <AttachmentUpload
          kind="topic"
          id={topic.id}
          onUploaded={() => queryClient.invalidateQueries({ queryKey: ['attachments', 'topic', topic.id] })}
        />
      )}
    </>
  );
}
